import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
    Card,
    Spinner,
    Alert,
    Badge,
    Row,
    Col,
    ListGroup,
} from 'react-bootstrap';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';

import { useGraphApi } from '@/shared/services/graphApi';
import { useOrganizationApi } from '@/shared/services/organizationApi';
import useChatPageEffect from '@/shared/hooks/useChatPageEffect';
import { useEcosystem } from '@/shared/contexts/EcosystemContext';

const SECTOR_LABELS = {
    'Non-profit': 'Non-profit',
    'PreK-12 education': 'PreK-12',
    'Post-secondary/Higher education': 'Higher Ed',
    'Business/Industry': 'Business',
    'Government/Policy maker': 'Government',
    'Non formal education': 'Non-formal Ed',
    'Coalition/Alliance': 'Coalition',
    'Foundation/Funder': 'Foundation',
    'Individual/Community': 'Individual',
    'Professional organization': 'Professional',
    'Medical/Health care': 'Health',
    'National lab': 'National Lab',
    Military: 'Military',
    Agriculture: 'Agriculture',
    'Sports organization': 'Sports',
};

// Color scale for heatmap cells
const HEATMAP_COLORS = [
    { min: 0, max: 0, className: 'heatmap-cell-0' },
    { min: 1, max: 5, className: 'heatmap-cell-1' },
    { min: 6, max: 15, className: 'heatmap-cell-2' },
    { min: 16, max: 30, className: 'heatmap-cell-3' },
    { min: 31, max: 50, className: 'heatmap-cell-4' },
    { min: 51, max: Infinity, className: 'heatmap-cell-5' },
];

// Bar chart colors
const SECTOR_BAR_COLOR = '#0d6efd';
const REGION_BAR_COLOR = '#198754';

function getSectorLabel(sector) {
    return SECTOR_LABELS[sector] || sector;
}

function getHeatmapClass(count) {
    for (const range of HEATMAP_COLORS) {
        if (count >= range.min && count <= range.max) {
            return range.className;
        }
    }
    return 'heatmap-cell-0';
}

function ExpandedRow({ sectorA, sectorB, graphApi }) {
    const [orgs, setOrgs] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchOrgs = async () => {
            try {
                const data = await graphApi.fetchPartnerships(sectorA, sectorB);
                setOrgs(data);
            } catch (err) {
                setError('Failed to load organizations');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchOrgs();
    }, [graphApi, sectorA, sectorB]);

    if (loading) {
        return (
            <div className="text-center py-3">
                <Spinner animation="border" size="sm" variant="primary" />
                <span className="ms-2 text-muted small">
                    Loading organizations...
                </span>
            </div>
        );
    }

    if (error) {
        return <p className="text-danger small mb-0 px-3 py-2">{error}</p>;
    }

    if (!orgs || orgs.edges?.length === 0) {
        return (
            <p className="text-muted small mb-0 px-3 py-2">
                No specific organization pairs found.
            </p>
        );
    }

    const sectorAOrgs = [
        ...new Map(
            orgs.nodes
                .filter((n) => n.primarySector === sectorA)
                .map((n) => [n.id, n])
        ).values(),
    ];
    const sectorBOrgs = [
        ...new Map(
            orgs.nodes
                .filter((n) => n.primarySector === sectorB)
                .map((n) => [n.id, n])
        ).values(),
    ];

    return (
        <div className="px-3 py-3">
            <div className="row g-3">
                <div className="col-md-6">
                    <h6 className="small fw-bold text-muted mb-2">
                        {getSectorLabel(sectorA)} ({sectorAOrgs.length})
                    </h6>
                    <ListGroup variant="flush" className="border rounded">
                        {sectorAOrgs.slice(0, 10).map((org) => (
                            <ListGroup.Item key={org.id} className="py-2">
                                <Link
                                    to={`/organizations/${org.id}`}
                                    className="text-decoration-none"
                                >
                                    {org.name}
                                </Link>
                            </ListGroup.Item>
                        ))}
                        {sectorAOrgs.length > 10 && (
                            <ListGroup.Item className="py-2 text-muted small">
                                +{sectorAOrgs.length - 10} more
                            </ListGroup.Item>
                        )}
                    </ListGroup>
                </div>
                <div className="col-md-6">
                    <h6 className="small fw-bold text-muted mb-2">
                        {getSectorLabel(sectorB)} ({sectorBOrgs.length})
                    </h6>
                    <ListGroup variant="flush" className="border rounded">
                        {sectorBOrgs.slice(0, 10).map((org) => (
                            <ListGroup.Item key={org.id} className="py-2">
                                <Link
                                    to={`/organizations/${org.id}`}
                                    className="text-decoration-none"
                                >
                                    {org.name}
                                </Link>
                            </ListGroup.Item>
                        ))}
                        {sectorBOrgs.length > 10 && (
                            <ListGroup.Item className="py-2 text-muted small">
                                +{sectorBOrgs.length - 10} more
                            </ListGroup.Item>
                        )}
                    </ListGroup>
                </div>
            </div>
            {orgs.edges.length > 0 && (
                <p className="text-muted small mt-2 mb-0">
                    {orgs.edges.length} direct partnership
                    {orgs.edges.length !== 1 ? 's' : ''} between these
                    organizations
                </p>
            )}
        </div>
    );
}

ExpandedRow.propTypes = {
    sectorA: PropTypes.string.isRequired,
    sectorB: PropTypes.string.isRequired,
    graphApi: PropTypes.object.isRequired,
};

export default function EcosystemAnalytics({ hiddenSections }) {
    const showStats = !hiddenSections.includes('stats');
    const showHeatmap = !hiddenSections.includes('heatmap');
    const showCharts = !hiddenSections.includes('charts');
    const showGaps = !hiddenSections.includes('gaps');

    const [connections, setConnections] = useState(null);
    const [graphStats, setGraphStats] = useState(null);
    const [distStats, setDistStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedCell, setExpandedCell] = useState(null);
    const { ecosystem } = useEcosystem();
    const ecosystemName = ecosystem?.name || 'the ecosystem';

    const graphApi = useGraphApi();
    const orgApi = useOrganizationApi();

    // Publish page context for chat widget
    useChatPageEffect(
        {
            page: 'ecosystem',
            totalOrganizations: distStats?.totalOrganizations,
            totalConnections: graphStats?.edgeCount,
            sectorCount: distStats?.sectorCount,
            regionCount: distStats?.regionCount,
        },
        [distStats, graphStats]
    );

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const [connData, gStats, dStats] = await Promise.all([
                    graphApi.fetchCollaborationGaps(),
                    graphApi.fetchStats(),
                    orgApi.fetchDistributionStats(),
                ]);
                setConnections(connData.connections);
                setGraphStats(gStats);
                setDistStats(dStats);
            } catch (err) {
                setError('Failed to load ecosystem analytics');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, [graphApi, orgApi]);

    const handleCellClick = useCallback((sectorA, sectorB, count) => {
        if (count === 0) return;
        setExpandedCell((prev) => {
            const key = `${sectorA}|${sectorB}`;
            return prev === key ? null : key;
        });
    }, []);

    // Build heatmap data structures
    const heatmapData = useMemo(() => {
        if (!connections) return null;

        const sectors = [
            ...new Set(connections.flatMap((d) => [d.sectorA, d.sectorB])),
        ].sort();

        // Build connection lookup
        const lookup = new Map();
        for (const d of connections) {
            const keyAB = `${d.sectorA}|${d.sectorB}`;
            const keyBA = `${d.sectorB}|${d.sectorA}`;
            lookup.set(keyAB, d.connectionCount);
            lookup.set(keyBA, d.connectionCount);
        }

        // Find gaps
        const gaps = [];
        for (let i = 0; i < sectors.length; i++) {
            for (let j = i + 1; j < sectors.length; j++) {
                const key = `${sectors[i]}|${sectors[j]}`;
                if (!lookup.has(key)) {
                    gaps.push({
                        sectorA: sectors[i],
                        sectorB: sectors[j],
                    });
                }
            }
        }

        return { sectors, lookup, gaps };
    }, [connections]);

    // Chart data
    const sectorChartData = useMemo(() => {
        if (!distStats?.bySector) return [];
        return distStats.bySector.map((s) => ({
            name: getSectorLabel(s.sector),
            fullName: s.sector,
            count: s.count,
        }));
    }, [distStats]);

    const regionChartData = useMemo(() => {
        if (!distStats?.byRegion) return [];
        return distStats.byRegion.map((r) => ({
            name: r.region,
            count: r.count,
        }));
    }, [distStats]);

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">
                    Loading ecosystem analytics...
                </p>
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    const totalConnections = graphStats?.edgeCount || 0;
    const totalOrgs = distStats?.totalOrganizations || 0;
    const sectorCount = distStats?.sectorCount || 0;
    const regionCount = distStats?.regionCount || 0;

    return (
        <div>
            <p className="text-muted mb-4">
                A bird&apos;s-eye view of {ecosystemName}: where partnerships
                are concentrated, which sectors are represented, and where new
                bridges could be built.
            </p>

            {/* Key Stats Row */}
            {showStats && (
                <Row className="g-3 mb-4">
                    <Col xs={6} md={3}>
                        <Card className="border-0 shadow-soft text-center h-100">
                            <Card.Body className="py-3">
                                <div className="ecosystem-stat-value text-primary">
                                    {totalOrgs}
                                </div>
                                <div className="small text-muted">
                                    Organizations
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={6} md={3}>
                        <Card className="border-0 shadow-soft text-center h-100">
                            <Card.Body className="py-3">
                                <div className="ecosystem-stat-value text-primary">
                                    {totalConnections.toLocaleString()}
                                </div>
                                <div className="small text-muted">
                                    Connections
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={6} md={3}>
                        <Card className="border-0 shadow-soft text-center h-100">
                            <Card.Body className="py-3">
                                <div className="ecosystem-stat-value text-primary">
                                    {sectorCount}
                                </div>
                                <div className="small text-muted">
                                    Active Sectors
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={6} md={3}>
                        <Card className="border-0 shadow-soft text-center h-100">
                            <Card.Body className="py-3">
                                <div className="ecosystem-stat-value text-primary">
                                    {regionCount}
                                </div>
                                <div className="small text-muted">
                                    Regions Covered
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Sector Connection Heatmap */}
            {showHeatmap && heatmapData && (
                <Card className="border-0 shadow-soft mb-4">
                    <Card.Header className="bg-white">
                        <strong>Sector Connection Heatmap</strong>
                        <span className="text-muted small ms-2">
                            Click a cell to see the organizations involved
                        </span>
                    </Card.Header>
                    <Card.Body className="p-3">
                        <div className="heatmap-container">
                            <div className="table-responsive">
                                <table className="heatmap-table">
                                    <thead>
                                        <tr>
                                            <th className="heatmap-corner" />
                                            {heatmapData.sectors.map((s) => (
                                                <th
                                                    key={s}
                                                    className="heatmap-col-header"
                                                >
                                                    <div className="heatmap-col-label">
                                                        {getSectorLabel(s)}
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {heatmapData.sectors.map(
                                            (rowSector, ri) => (
                                                <React.Fragment key={rowSector}>
                                                    <tr>
                                                        <td className="heatmap-row-header">
                                                            {getSectorLabel(
                                                                rowSector
                                                            )}
                                                        </td>
                                                        {heatmapData.sectors.map(
                                                            (colSector, ci) => {
                                                                if (ri === ci) {
                                                                    return (
                                                                        <td
                                                                            key={
                                                                                colSector
                                                                            }
                                                                            className="heatmap-cell heatmap-cell-self"
                                                                        >
                                                                            &mdash;
                                                                        </td>
                                                                    );
                                                                }
                                                                const key = `${rowSector}|${colSector}`;
                                                                const count =
                                                                    heatmapData.lookup.get(
                                                                        key
                                                                    ) || 0;
                                                                const cellClass =
                                                                    getHeatmapClass(
                                                                        count
                                                                    );
                                                                const isExpanded =
                                                                    expandedCell ===
                                                                        key ||
                                                                    expandedCell ===
                                                                        `${colSector}|${rowSector}`;
                                                                return (
                                                                    <td
                                                                        key={
                                                                            colSector
                                                                        }
                                                                        className={`heatmap-cell ${cellClass} ${count > 0 ? 'heatmap-cell-clickable' : ''} ${isExpanded ? 'heatmap-cell-active' : ''}`}
                                                                        onClick={() =>
                                                                            handleCellClick(
                                                                                rowSector,
                                                                                colSector,
                                                                                count
                                                                            )
                                                                        }
                                                                        title={`${getSectorLabel(rowSector)} ↔ ${getSectorLabel(colSector)}: ${count} connections`}
                                                                    >
                                                                        {count}
                                                                    </td>
                                                                );
                                                            }
                                                        )}
                                                    </tr>
                                                    {/* Expanded detail row */}
                                                    {heatmapData.sectors.some(
                                                        (colSector) => {
                                                            const key = `${rowSector}|${colSector}`;
                                                            const keyRev = `${colSector}|${rowSector}`;
                                                            return (
                                                                expandedCell ===
                                                                    key ||
                                                                expandedCell ===
                                                                    keyRev
                                                            );
                                                        }
                                                    ) &&
                                                        (() => {
                                                            const matchCol =
                                                                heatmapData.sectors.find(
                                                                    (
                                                                        colSector
                                                                    ) => {
                                                                        const key = `${rowSector}|${colSector}`;
                                                                        return (
                                                                            expandedCell ===
                                                                            key
                                                                        );
                                                                    }
                                                                );
                                                            if (!matchCol) {
                                                                return null;
                                                            }
                                                            return (
                                                                <tr
                                                                    key={`${rowSector}-expanded`}
                                                                >
                                                                    <td
                                                                        colSpan={
                                                                            heatmapData
                                                                                .sectors
                                                                                .length +
                                                                            1
                                                                        }
                                                                        className="p-0 border-0 bg-light"
                                                                    >
                                                                        <ExpandedRow
                                                                            sectorA={
                                                                                rowSector
                                                                            }
                                                                            sectorB={
                                                                                matchCol
                                                                            }
                                                                            graphApi={
                                                                                graphApi
                                                                            }
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })()}
                                                </React.Fragment>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Heatmap Legend */}
                        <div className="d-flex align-items-center gap-2 mt-3">
                            <span className="small text-muted">Fewer</span>
                            <div className="heatmap-legend-cell heatmap-cell-0" />
                            <div className="heatmap-legend-cell heatmap-cell-1" />
                            <div className="heatmap-legend-cell heatmap-cell-2" />
                            <div className="heatmap-legend-cell heatmap-cell-3" />
                            <div className="heatmap-legend-cell heatmap-cell-4" />
                            <div className="heatmap-legend-cell heatmap-cell-5" />
                            <span className="small text-muted">More</span>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Charts Row */}
            {showCharts && (
                <Row className="g-4 mb-4">
                    {/* Sector Distribution */}
                    <Col lg={6}>
                        <Card className="border-0 shadow-soft h-100">
                            <Card.Header className="bg-white">
                                <strong>Organizations by Sector</strong>
                            </Card.Header>
                            <Card.Body>
                                {sectorChartData.length > 0 ? (
                                    <ResponsiveContainer
                                        width="100%"
                                        height={
                                            sectorChartData.length * 32 + 20
                                        }
                                    >
                                        <BarChart
                                            data={sectorChartData}
                                            layout="vertical"
                                            margin={{
                                                top: 5,
                                                right: 30,
                                                left: 10,
                                                bottom: 5,
                                            }}
                                        >
                                            <XAxis type="number" />
                                            <YAxis
                                                type="category"
                                                dataKey="name"
                                                width={100}
                                                tick={{ fontSize: 12 }}
                                            />
                                            <Tooltip
                                                formatter={(
                                                    value,
                                                    name,
                                                    entry
                                                ) => [
                                                    `${value} organizations`,
                                                    entry.payload.fullName ||
                                                        entry.payload.name,
                                                ]}
                                            />
                                            <Bar
                                                dataKey="count"
                                                fill={SECTOR_BAR_COLOR}
                                                radius={[0, 4, 4, 0]}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <p className="text-muted text-center py-3">
                                        No sector data available
                                    </p>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Regional Coverage */}
                    <Col lg={6}>
                        <Card className="border-0 shadow-soft h-100">
                            <Card.Header className="bg-white">
                                <strong>Organizations by Region</strong>
                            </Card.Header>
                            <Card.Body>
                                {regionChartData.length > 0 ? (
                                    <ResponsiveContainer
                                        width="100%"
                                        height={
                                            regionChartData.length * 32 + 20
                                        }
                                    >
                                        <BarChart
                                            data={regionChartData}
                                            layout="vertical"
                                            margin={{
                                                top: 5,
                                                right: 30,
                                                left: 10,
                                                bottom: 5,
                                            }}
                                        >
                                            <XAxis type="number" />
                                            <YAxis
                                                type="category"
                                                dataKey="name"
                                                width={120}
                                                tick={{ fontSize: 12 }}
                                            />
                                            <Tooltip
                                                formatter={(value) => [
                                                    `${value} organizations`,
                                                    'Count',
                                                ]}
                                            />
                                            <Bar
                                                dataKey="count"
                                                fill={REGION_BAR_COLOR}
                                                radius={[0, 4, 4, 0]}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <p className="text-muted text-center py-3">
                                        No region data available
                                    </p>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Collaboration Gaps */}
            {showGaps && heatmapData?.gaps.length > 0 && (
                <Card className="border-0 shadow-soft">
                    <Card.Header className="bg-white">
                        <strong>Opportunities for Growth</strong>
                        <Badge bg="info" className="ms-2">
                            {heatmapData.gaps.length} sector pairs
                        </Badge>
                    </Card.Header>
                    <Card.Body>
                        <p className="text-muted small mb-3">
                            These sector pairs have no existing connections.
                            Click a sector name to browse organizations in that
                            sector and find potential partners.
                        </p>
                        <div className="d-flex flex-wrap gap-2">
                            {heatmapData.gaps.map((gap, i) => (
                                <div
                                    key={i}
                                    className="gap-badge d-inline-flex align-items-center border rounded p-2"
                                >
                                    <Link
                                        to={`/directory?view=cards&sector=${encodeURIComponent(gap.sectorA)}`}
                                        className="gap-badge-link"
                                    >
                                        {getSectorLabel(gap.sectorA)}
                                    </Link>
                                    <span className="text-muted mx-1">↔</span>
                                    <Link
                                        to={`/directory?view=cards&sector=${encodeURIComponent(gap.sectorB)}`}
                                        className="gap-badge-link"
                                    >
                                        {getSectorLabel(gap.sectorB)}
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </Card.Body>
                </Card>
            )}
        </div>
    );
}

EcosystemAnalytics.propTypes = {
    hiddenSections: PropTypes.arrayOf(PropTypes.string),
};

EcosystemAnalytics.defaultProps = {
    hiddenSections: [],
};
